import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { useAPI } from 'hooks/api/useAPI';

import APIError from 'types/api_error';
import {
  Deployment,
  DeploymentConfiguration,
  DeploymentState,
} from 'types/deployment';

import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';

type UseUpdateDeploymentParams = {
  id: string;
} & (
  | {
      name: string;
      state?: DeploymentState;
      configuration?: DeploymentConfiguration;
    }
  | {
      name?: string;
      state: DeploymentState;
      configuration?: DeploymentConfiguration;
    }
  | {
      name?: string;
      state?: DeploymentState;
      configuration: DeploymentConfiguration;
    }
);

export function useUpdateDeployment({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<
  Deployment,
  APIError,
  UseUpdateDeploymentParams,
  UpdateQueriesDataContext<Deployment>
> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ id, ...params }) => {
      const { data } = await client.put(
        `/apps/${applicationID}/deployments/${id}`,
        { ...params }
      );

      return data;
    },
    {
      ...options,
      async onMutate({ id, ...params }) {
        await queryClient.cancelQueries(['deployments']);
        await queryClient.cancelQueries(['deployment']);

        onMutate?.({ id, ...params });

        return updateQueriesData({
          queryClient,
          listQueryKey: ['deployments'],
          singleQueryKey: ['deployment'],
          ids: [id],
          updateData: (deployment) => ({ ...deployment, ...params }),
        });
      },
      onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['deployments']);
        queryClient.invalidateQueries(['deployment']);
      },
    }
  );
}
